import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	CumulativePercentage,
	CumulativePercentageGraphData,
} from 'features/groups/analysis/redux/types/cumulativePercentage';
import { selectCumulativePercentageOptions } from 'features/groups/analysis/ks5pm/res/staticValues';

/**
 * Initial State
 */

const initialState: CumulativePercentage.State = {
	loading: false,
	exportExcel: false,
	examLevels: undefined,
	grids: undefined,
	rows: undefined,
	error: undefined,
	graphLoading: true,
	graphExamLevels: undefined,
	graphs: undefined,
	xAxis: undefined,
	graphError: undefined,
	activeOutcomeGroup: selectCumulativePercentageOptions[0],
};

/**
 * Reducers
 */
const cumulativePercentage = createSlice({
	name: 'cumulativePercentage',
	initialState,
	reducers: {
		setLoading(state: CumulativePercentage.State, action: PayloadAction<boolean>) {
			return {
				...state,
				loading: action.payload,
			};
		},
		setGridDataSuccess(
			state: CumulativePercentage.State,
			action: PayloadAction<CumulativePercentage.GridData>
		) {
			const { examLevels, grids, rows } = action.payload;
			return {
				...state,
				examLevels,
				grids,
				rows,
				loading: false,
				error: undefined,
			};
		},
		setGraphLoading(state: CumulativePercentage.State, action: PayloadAction<boolean>) {
			return {
				...state,
				graphLoading: action.payload,
			};
		},
		setMainGraphDataSuccess(
			state: CumulativePercentage.State,
			action: PayloadAction<CumulativePercentageGraphData.GraphData>
		) {
			const { examLevels, graphs, xAxis } = action.payload;

			// If this is the first fetch just set the data
			if (!state.graphs || !state.graphExamLevels) {
				return {
					...state,
					examLevels,
					graphError: undefined,
					graphs,
					xAxis,
					graphLoading: false,
					loading: false,
				};
			}
			// If this is a subsequent fetch update the data
			return {
				...state,
				graphError: undefined,
				graphLoading: false,
				graphExamLevels: {
					...state.graphExamLevels,
					id: examLevels.id,
					byId: {
						...state.graphExamLevels!.byId,
						...examLevels.byId,
					},
					//@ts-ignore bloody spread error again
					allIds: [...state.graphExamLevels!.allIds, ...examLevels.allIds],
				},
				graphs: {
					...state.graphs,
					id: graphs.id,
					byId: {
						...state.graphs?.byId,
						...graphs.byId,
					},
					allIds: [...state.graphs!.allIds, ...graphs.allIds],
				},
				xAxis: {
					...state.xAxis,
					id: xAxis.id,
					byId: {
						...state.xAxis!.byId,
						...xAxis.byId,
					},
					allIds: [...state.xAxis!.allIds, ...xAxis.allIds],
				},
			};
		},
		handleExcelExport(state: CumulativePercentage.State, action: PayloadAction<boolean>) {
			return {
				...state,
				exportExcel: action.payload,
			};
		},
		setError(state: CumulativePercentage.State, action: PayloadAction<string | undefined>) {
			return {
				...state,
				error: action.payload,
			};
		},
		setGraphError(state: CumulativePercentage.State, action: PayloadAction<string | undefined>) {
			return {
				...state,
				graphError: action.payload,
			};
		},
		resetCumulativePercentageState(state: CumulativePercentage.State) {
			return initialState;
		},
		setActiveOutcomeGroup(
			state: CumulativePercentage.State,
			action: PayloadAction<CommonUiTypes.SelectOption | undefined>
		) {
			return {
				...state,
				activeOutcomeGroup: action.payload,
			};
		},
	},
});

/**
 ** Export Actions
 */
export const {
	setLoading,
	setGraphLoading,
	setGridDataSuccess,
	setMainGraphDataSuccess,
	handleExcelExport,
	setError,
	setGraphError,
	resetCumulativePercentageState,
	setActiveOutcomeGroup,
} = cumulativePercentage.actions;

/**
 ** Export Reducers
 */
export default cumulativePercentage.reducer;
